import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormField } from "@colab/shared";
import type { AdminFormRow } from "./types";

export type ReorderFieldsInput = {
  formId: string;
  sections: Array<{ id: string; fieldIds: string[] }>;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// PATCH /api/admin/forms/:formId/fields/order
//
// Optimistic — rebuilds detail.definition.sections from the new ordering,
// preserving every field's non-id properties. One PATCH per drop (the
// established DnD convention for this codebase; bucket 11 inherits).
export function useReorderFields() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ReorderFieldsInput, Context>({
    mutationFn: ({ formId, sections }) =>
      apiFetch<{ ok: true }>(`/admin/forms/${formId}/fields/order`, {
        method: "PATCH",
        body: JSON.stringify({ sections }),
      }),
    onMutate: async ({ formId, sections }) => {
      const detailKey = queryKeys.adminForms.detail(formId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) => {
        if (!old) return old;
        // Build a flat fieldId -> FormField map off the current definition so
        // the new ordering can be reassembled without losing per-field config.
        const fieldById = new Map<string, FormField>();
        const sectionById = new Map(
          old.definition.sections.map((s) => [s.id, s] as const),
        );
        for (const s of old.definition.sections) {
          for (const f of s.fields) fieldById.set(f.id, f);
        }
        const nextSections = sections.map(({ id, fieldIds }) => {
          const existing = sectionById.get(id);
          // Drop the optimistic reorder on the floor if a section id is
          // unknown — the onSettled invalidate will refetch and the server's
          // validation will surface the real error via the toast.
          if (!existing) return null;
          return {
            ...existing,
            fields: fieldIds
              .map((fid) => fieldById.get(fid))
              .filter((f): f is FormField => f !== undefined),
          };
        });
        if (nextSections.some((s) => s === null)) return old;
        return {
          ...old,
          definition: {
            ...old.definition,
            sections: nextSections as AdminFormRow["definition"]["sections"],
          },
        };
      });
      return { previousDetail };
    },
    onError: (err, { formId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.adminForms.detail(formId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to reorder fields");
    },
    onSettled: (_data, _err, { formId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminForms.detail(formId),
      });
    },
  });
}
